import React, { FC, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateCrmRuleRequestDto } from '@sparling/financial-common'
import { TransparentButton } from '../../../../../components/Buttons/TransparentButton'
import { routes } from '../../../../../constants/routes'
import { GreenButton } from '../../../../../components/Buttons/GreenButton'
import { InputField } from '../../../../../components/InputField'
import { InputSelect } from '../../../../../components/InputSelect'
import {
  riskScoreTypes,
  riskScoreOperators,
  riskScoreLevels,
  finalRiskScoreTypes,
} from '../../../../../constants/riskScoreRuleOptions'
import { getRiskScoreRuleRequest, updateRiskScoreRuleRequest } from '../../../../../store/actions/crm-rules'
import { getCrmRuleDetails, riskScoreRuleDetailsLoading } from '../../../../../store/selectors'
import { Loader } from '../../../../../components/Loader'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  sectionWrap: {
    width: '100%',
    margin: '32px 0',
    padding: theme.spacing(3),
    background: 'rgba(216, 216, 216, 0.05)',
  },
  inputWrap: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  buttonWrap: {
    justifyContent: 'space-between',
    width: 256,
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  headerRow: {
    color: '#9b9c9f',
    marginBottom: theme.spacing(1),
  },
  rowWrap: {
    alignItems: 'center',
  },
  addMoreWrap: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: '#9b9c9f',
  },
}))

export const EditRule: FC = () => {
  const classes = useStyles()
  const { id, tab } = useParams<{ id: string; tab?: string }>()
  const dispatch = useDispatch()
  const history = useHistory()
  const { data: selectedRule } = useSelector(getCrmRuleDetails)
  const isLoading = useSelector(riskScoreRuleDetailsLoading)

  const handleBack = () => history.goBack()

  const formMethods = useForm<UpdateCrmRuleRequestDto>()
  const { handleSubmit, control, reset } = formMethods

  const whenArray = useFieldArray({ control, name: 'when' })
  const thenArray = useFieldArray({ control, name: 'then' })

  useEffect(() => {
    if (id) {
      dispatch(getRiskScoreRuleRequest({ id: Number(id) }))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (selectedRule) {
      reset(selectedRule)
    }
  }, [selectedRule, reset])

  const onSubmit = (data: UpdateCrmRuleRequestDto) => {
    dispatch(updateRiskScoreRuleRequest({ ...data, id: Number(id) }))
    history.push(`${routes.SETTINGS_RULES}/${tab}`)
  }

  if (isLoading || !selectedRule) {
    return <Loader />
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.topWrap}>
          <Typography variant="h1" className={classes.title}>
            Edit Rule
          </Typography>
          <Grid container className={classes.buttonWrap}>
            <TransparentButton width={120} onClick={handleBack} text="Cancel" />
            <GreenButton width={120} text="Save" type="submit" />
          </Grid>
        </Grid>

        <Grid item container className={classes.sectionWrap}>
          <Grid item container className={classes.inputWrap}>
            <InputField inputName={'name'} labelName={'Rule Name'} inputType={'text'} fullWidth />
          </Grid>
        </Grid>

        <Grid item container className={classes.sectionWrap}>
          <Typography variant="h5" className={classes.sectionHeader}>
            When
          </Typography>
          <Grid container className={classes.headerRow} spacing={2}>
            <Grid item xs={5}>
              <Typography variant="body2">Score Type</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2">Operator</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2">Score Level</Typography>
            </Grid>
          </Grid>
          {whenArray.fields.map((field, index) => (
            <Grid key={field.id} container className={classes.rowWrap} spacing={2}>
              <Grid item xs={5}>
                <InputSelect inputName={`when[${index}].type`} fullWidth enums={riskScoreTypes} />
              </Grid>
              <Grid item xs={3}>
                <InputSelect inputName={`when[${index}].operator`} fullWidth enums={riskScoreOperators} />
              </Grid>
              <Grid item xs={4}>
                <InputSelect inputName={`when[${index}].level`} fullWidth enums={riskScoreLevels} />
              </Grid>
            </Grid>
          ))}
          <Grid container className={classes.rowWrap}>
            <div className={classes.addMoreWrap}>
              <Typography>Condition</Typography>
              <TransparentButton
                width={160}
                text="Add Row"
                padding="20px"
                onClick={() => whenArray.append({ type: 'Client risk score', operator: 'equal to', level: 'High' })}
              />
            </div>
          </Grid>
        </Grid>

        <Grid item container className={classes.sectionWrap}>
          <Typography variant="h5" className={classes.sectionHeader}>
            Then
          </Typography>
          <Grid container className={classes.headerRow} spacing={2}>
            <Grid item xs={5}>
              <Typography variant="body2">Action Type</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2">Operator</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2">Status</Typography>
            </Grid>
          </Grid>
          {thenArray.fields.map((field, index) => (
            <Grid key={field.id} container className={classes.rowWrap} spacing={2}>
              <Grid item xs={5}>
                <InputSelect inputName={`then[${index}].type`} fullWidth enums={finalRiskScoreTypes} />
              </Grid>
              <Grid item xs={3}>
                <InputSelect inputName={`then[${index}].operator`} fullWidth enums={riskScoreOperators} />
              </Grid>
              <Grid item xs={4}>
                <InputSelect inputName={`then[${index}].level`} fullWidth enums={riskScoreLevels} />
              </Grid>
            </Grid>
          ))}
          <Grid container className={classes.rowWrap}>
            <div className={classes.addMoreWrap}>
              <Typography>Condition</Typography>
              <TransparentButton
                width={160}
                text="Add Row"
                padding="20px"
                onClick={() => thenArray.append({ type: 'Client risk score', operator: 'equal to', level: 'High' })}
              />
            </div>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
